import { Box, Button, Stack, Typography } from "@mui/material";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "@/lib/i18n";
import { CenteredContent, PageContainer, PageHeader } from "@/features/admin/components";
import {
  LocationSettingsDialog,
  LocationsList,
} from "@/features/admin/features/dashboards";
import InfoSettings from "@/features/admin/features/dashboards/components/info-settings/info-settings";
import TimeSettings from "@/features/admin/features/dashboards/components/time-settings/time-settings";
import WarningsSettingsDialog from "@/features/admin/features/dashboards/components/warnings-dialog-box";
import MaritimeWarningsSettings from "@/features/admin/features/dashboards/components/maritime-warnings-settings/maritime-warnings-settings";
import WarningPagesSettings from "@/features/admin/features/dashboards/components/warning-pages-settings/warning-pages-settings";
import WarningData from "@/features/admin/features/dashboards/components/warnings-dialog-box/warningsData";
import { useAdminDashboard, useDeleteDashboardMutation } from "@/features/admin/hooks";
import { ModalComponent, confirm, showToast } from "@/features/shared/components";

const DashboardSettingsPage = () => {
  const { t } = useTranslation();
  const deleteDashboardMutation = useDeleteDashboardMutation();
  const navigate = useNavigate();
  const dashboard = useAdminDashboard();

  const { id } = dashboard;

  const locationSettingRef = useRef<ModalComponent>();
  const warningRef = useRef<ModalComponent>();

  const removeDashboard = async () => {
    const result = await confirm(
      t("admin.dashboard_settings.remove_dashboard.confirm.title"),
      t("admin.dashboard_settings.remove_dashboard.confirm.body"),
    );

    if (!result) return;

    deleteDashboardMutation.mutate(id, {
      onSuccess: () => {
        showToast(t("admin.messages.dashboard_deleted"));
        navigate("/admin");
      },
    });
  };

  return (
    <PageContainer>
      <PageHeader title={t("admin.dashboard_settings.title")} />
      <CenteredContent
        title={t("admin.dashboard_settings.general_title")}
        sx={{ minHeight: 200 }}
      >
        <InfoSettings />
      </CenteredContent>

      <CenteredContent
        title={t("admin.dashboard_settings.time_settings.title")}
        sx={{ minHeight: 0 }}
      >
        <TimeSettings />
      </CenteredContent>

      <CenteredContent
        sx={{ minHeight: 200 }}
        title={t("admin.dashboard_settings.locations_title")}
        right={
          <>
            <Button variant="outlined" onClick={() => locationSettingRef.current?.open?.()}>
              Nieuwe locatie
            </Button>
            <LocationSettingsDialog ref={locationSettingRef} isNewLocation />
          </>
        }
      >
        <Box sx={{ paddingTop: 2, paddingBottom: 2 }}>
          <LocationsList />
        </Box>
      </CenteredContent>

      <CenteredContent
        sx={{ minHeight: 200 }}
        title={t("admin.dashboard_settings.warnings_settings.title")}
        right={
          <>
            <Button variant="outlined" onClick={() => warningRef.current?.open?.()}>
              Nieuwe waarschuwing
            </Button>
            <WarningsSettingsDialog ref={warningRef} isNewWarning />
          </>
        }
      >
        <WarningData />
      </CenteredContent>

      <CenteredContent
        sx={{ minHeight: 0 }}
        title={t("admin.dashboard_settings.maritime_warnings.title")}
      >
        <MaritimeWarningsSettings />
      </CenteredContent>

      <CenteredContent
        sx={{ minHeight: 0 }}
        title={t("admin.dashboard_settings.warning_pages.title")}
      >
        <WarningPagesSettings />
      </CenteredContent>

      <CenteredContent
        sx={{ minHeight: 200 }}
        color="error"
        title={t("admin.dashboard_settings.remove_dashboard.title")}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ mt: 6, mb: 4, px: "2px" }}
        >
          <Typography color="error">
            {t("admin.dashboard_settings.delete_title")}
          </Typography>

          <Button onClick={removeDashboard} color="error" variant="outlined">
            {t("admin.dashboard_settings.remove_dashboard.button")}
          </Button>
        </Stack>
      </CenteredContent>
    </PageContainer>
  );
};

export default DashboardSettingsPage;
